import { findUsStateMatch } from "~/pages/Compliance/documents/CaseCreationPreview/stateMatcher";

/**
 * Utility functions for comparing profile data with extracted data from documents
 */

export type ProfileDataField = {
  dateOfBirth?: string | null;
  dateOfOrganization?: string | null;
  street?: string | null;
  street2?: string | null;
  street3?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

/**
 * Type for agent-extracted data from documents.
 * Since this comes from a JSON blob in GraphQL, we use a flexible Record type
 * that can accommodate different document types and extraction schemas.
 */
export type ExtractedDataField = Record<string, string>;

export type DataDifference = {
  field: string;
  currentValue: string;
  extractedValue: string;
  isDifferent: boolean;
};

/**
 * Normalizes a value for comparison by trimming whitespace and converting to lowercase
 */
const normalizeValue = (value: string): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return value.trim().toLowerCase();
};

/**
 * Compares two values and returns whether they are different
 */
export const compareValues = (currentValue: string, extractedValue: string): boolean => {
  const normalizedCurrent = normalizeValue(currentValue);
  const normalizedExtracted = normalizeValue(extractedValue);

  // Both empty is considered the same
  if (normalizedCurrent === "" && normalizedExtracted === "") {
    return false;
  }

  return normalizedCurrent !== normalizedExtracted;
};

/*
 * Gets a value from extracted data object by key
 */
const getExtractedValue = (extractedData: ExtractedDataField, key: string): string => {
  const value = extractedData[key];
  return typeof value === "string" ? value : "";
};

/**
 * Formats street address from extracted data (for internal comparison)
 * Returns empty string if no address parts are available
 */
const getExtractedStreetAddress = (extractedData: ExtractedDataField): string => {
  const street = getExtractedValue(extractedData, "street");
  const street2 = getExtractedValue(extractedData, "street2");
  const parts = [street, street2].filter((part) => part !== "");
  return parts.length > 0 ? parts.join(", ") : "";
};

/**
 * Formats street address from profile data
 */
const getProfileStreetAddress = (profileData: ProfileDataField): string => {
  const parts = [profileData.street, profileData.street2, profileData.street3].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "";
};

/**
 * Compares profile data with extracted data and returns all differences
 */
export const compareProfileWithExtractedData = (
  profileData: ProfileDataField,
  extractedData: ExtractedDataField,
  isIndividual: boolean,
): number => {
  let differenceCount = 0;
  if (isIndividual) {
    const currentDOB = profileData.dateOfBirth || "";
    const extractedDOB = getExtractedValue(extractedData, "date_of_birth");
    if (compareValues(currentDOB, extractedDOB)) differenceCount++;
  } else {
    const currentDOO = profileData.dateOfOrganization || "";
    const extractedDOO = getExtractedValue(extractedData, "date_of_organization");
    if (compareValues(currentDOO, extractedDOO)) differenceCount++;
  }

  const currentStreet = getProfileStreetAddress(profileData);
  const extractedStreet = getExtractedStreetAddress(extractedData);
  if (compareValues(currentStreet, extractedStreet)) differenceCount++;

  const currentCity = profileData.city || "";
  const extractedCity = getExtractedValue(extractedData, "city");
  if (compareValues(currentCity, extractedCity)) differenceCount++;

  const currentState = profileData.state || "";
  const extractedState = getExtractedValue(extractedData, "state");
  const currentStateCode = findUsStateMatch(currentState);
  const extractedStateCode = findUsStateMatch(extractedState);
  if (currentStateCode && extractedStateCode) {
    if (currentStateCode !== extractedStateCode) differenceCount++;
  } else if (compareValues(currentState, extractedState)) {
    differenceCount++;
  }

  const currentPostalCode = profileData.postalCode || "";
  const extractedPostalCode = getExtractedValue(extractedData, "postal_code");
  if (compareValues(currentPostalCode, extractedPostalCode)) differenceCount++;

  return differenceCount;
};
